import { Component, OnDestroy, OnInit } from '@angular/core';
import { Account, IUser } from '@app/account/account.models';
import { AuthService } from '@app/services/auth.service';
import { IAppState } from '@app/store';
import { getCurrentUserWithAccount } from '@app/store/user';
import { environment } from '@env/environment';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LiveChatService } from '../live-chat.service';
import { Actions, ofType } from '@ngrx/effects';
import { AuthActionType } from '@app/store/auth';
import { SupportActionType } from '@app/store/support';

@Component({
    selector: 'c4-live-chat-window',
    templateUrl: './live-chat-window.component.html',
    styleUrls: ['./live-chat-window.component.scss']
})
export class LiveChatWindowComponent
    implements OnInit, OnDestroy {

    currentUser: IUser;
    currentAccount: Account;

    chatButtonEnabled = false;
    chatButtonVisible = false;

    private destroyed$ = new Subject<void>();

    //============================================================
    // CXONE configuration
    //============================================================

    private CXONE_LOADER_URL = '';
    private CXONE_TENANT_ID = '';

    // Control4 chat hours - Eastern Time
    private readonly CXONE_START_HOUR = environment.NiceChatStartHour;
    private readonly CXONE_START_MINUTE = environment.NiceChatStartMinute;
    private readonly CXONE_END_HOUR = environment.NiceChatEndHour;
    private readonly CXONE_END_MINUTE = environment.NiceChatEndMinute;

    //============================================================
    // Chat state
    //============================================================

    private cxoneLoading = false;
    private cxoneInitialized = false;

    private cxoneState: 'pending' | 'online' | 'offline' = 'pending';
    private cxoneStateTimer: ReturnType<typeof setTimeout> | null = null;
    private cxoneStyleElement: HTMLStyleElement | null = null;

    constructor(
        private store: Store<IAppState>,
        private authService: AuthService,
        private liveChatService: LiveChatService,
        private actions$: Actions
    ) {

        this.liveChatService.hideChat$.subscribe((hideChat: boolean) => {
                if (hideChat) {
                    setTimeout(() => { this.handleUserLogout(); }, 1000);
                } else {
                    this.handleUserLogin();
                }
            });
    }

    ngOnInit(): void {

        this.loadConfiguration();

        this.store.pipe(
            select(getCurrentUserWithAccount),
            takeUntil(this.destroyed$)
        ).subscribe((user: IUser) => {

            this.currentUser = user;
            this.currentAccount = user
                ? user.account
                : undefined;

            if (this.currentUser && this.currentAccount) {
                this.startCxoneAvailabilityCheck();
                this.listenForChatToggle();
            }
        });

        console.log('[CXone] Component initialized');
    }

    /**
    * Handles the LiveChatToggle NgRx action Chat button Action.
    * click the CXone channel button to open the chat.
    */
    private listenForChatToggle(): void {
        this.actions$
            .pipe(
                ofType(SupportActionType.LiveChatToggle),
                takeUntil(this.destroyed$)
            )
            .subscribe(() => {
                console.log('[CXone] LiveChatToggle triggered');
                const button = document.querySelector('[data-selector="GUIDE_CHANNEL_BUTTON"]') as HTMLElement;
                if (button) {
                    button.click();
                }
        });
    }

    //============================================================
    // Handle user login
    //============================================================

    private handleUserLogin(): void {

        if (this.cxoneStyleElement) {
            this.cxoneStyleElement.remove();
            this.cxoneStyleElement = null;
        }

        console.log('[CXone] Nice chat button visible');
    }

    //============================================================
    // Handle user logout
    //============================================================

    private handleUserLogout(): void {

        if (this.cxoneStyleElement) {
            return;
        }

        const style = document.createElement('style');

        style.id = 'cxone-offline-style';

        style.textContent = `
        [data-selector="GUIDE_CHANNEL_BUTTON"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
        }

        [data-selector="GUIDE_CHANNEL_ICON"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
        }`;

        document.head.appendChild(style);

        this.cxoneStyleElement = style;

        console.log('[CXone] Nice chat button hidden');
    }

    //============================================================
    // Load configuration
    //============================================================

    loadConfiguration(): void {

        this.CXONE_LOADER_URL =
            (environment as typeof environment & {
                NiceChatLoaderUrl?: string;
            }).NiceChatLoaderUrl || '';

        this.CXONE_TENANT_ID =
            (environment as typeof environment & {
                NiceChatTENANTID?: string;
            }).NiceChatTENANTID || '';
    }

    ngOnDestroy(): void {

        console.log('[CXone] Component destroyed');

        this.destroyed$.next();
        this.destroyed$.complete();

        this.stopCxoneAvailabilityCheck();

        this.hideCxoneOfflineBanner();

        this.removeCxoneOfflineStyle();
    }

    //============================================================
    // Start availability checking
    //============================================================

    private startCxoneAvailabilityCheck(): void {

        this.stopCxoneAvailabilityCheck();

        this.checkCxoneAvailability();
    }

    //============================================================
    // Main availability logic
    //============================================================

    private checkCxoneAvailability(): void {

        const now = new Date();

        const etTime = this.getEasternTime(now);

        const hour = etTime.hour;
        const minute = etTime.minute;

        const currentMinutes =
            (hour * 60) + minute;

        const startMinutes =
            (this.CXONE_START_HOUR * 60) +
            this.CXONE_START_MINUTE;

        const endMinutes =
            (this.CXONE_END_HOUR * 60) +
            this.CXONE_END_MINUTE;

        console.log(
            `[CXone] Current ET time: ${this.formatTime(hour, minute)}`
        );

        //==========================================================
        // WEEKEND CHECK
        //==========================================================

        const dayOfWeek = etTime.dayOfWeek;

        /*
         * JavaScript:
         * 0 = Sunday
         * 1 = Monday
         * 2 = Tuesday
         * 3 = Wednesday
         * 4 = Thursday
         * 5 = Friday
         * 6 = Saturday
         */

        // Weekend testing code
        const isWeekend =
            dayOfWeek === 0 ||
            dayOfWeek === 6;

        if (isWeekend) {

            console.log(
                '[CXone] Weekend - chat unavailable'
            );

            this.liveChatService.disableChat();

            this.setCxoneOffline('weekend');

            /*
             * Check again tomorrow.
             */
            this.scheduleNextCxoneDayCheck();

            return;
        }

        //==========================================================
        // Outside Control4 chat hours
        //==========================================================

        if (
            currentMinutes < startMinutes ||
            currentMinutes >= endMinutes
        ) {

            console.log(
                '[CXone] Outside chat hours'
            );

            this.liveChatService.disableChat();

            this.setCxoneOffline('offline_hours');

            this.scheduleNextCxoneTimeCheck(
                currentMinutes,
                startMinutes,
                endMinutes
            );

            return;
        }

        //==========================================================
        // Inside Control4 chat hours
        //==========================================================

        console.log(
            '[CXone] Inside Control4 chat hours'
        );

        this.liveChatService.enableChat();

        this.checkControl4AgentAvailability();
    }

    //============================================================
    // Control4 agent availability
    //============================================================

    private checkControl4AgentAvailability(): void {

        /*
        * Replace this with your actual CXONE/Control4
        * agent availability API.
        */

        const agentsAvailable = true;

        if (agentsAvailable) {

            console.log(
                '[CXone] Control4 agents are available'
            );

            this.setCxoneOnline();

        } else {

            console.log(
                '[CXone] No Control4 agents are available'
            );

            this.setCxoneOffline(
                'offline_setting'
            );
        }

        // Check again in 5 minutes
        this.scheduleAgentAvailabilityCheck();
    }

    //============================================================
    // Set CXONE online
    //============================================================

    private setCxoneOnline(): void {

        console.log(
            '[CXone] Setting status to ONLINE'
        );

        this.cxoneState = 'online';

        // Enable Angular chat button
        this.enableChatButton();

        // Show CXONE chat icon
        this.showCxoneChat();

        // Remove offline banner if one exists
        this.hideCxoneOfflineBanner();

        // Load CXONE
        this.loadCxoneChatWindow();
    }

    //============================================================
    // Set CXONE offline
    //============================================================

    private setCxoneOffline(
        reason: string
    ): void {

        console.log(
            '[CXone] Setting status to OFFLINE:',
            reason
        );

        this.cxoneState = 'offline';

        // Disable chat button
        this.disableChatButton();

        // Hide CXONE chat
        this.hideCxoneChat();

        // Remove offline banner
        this.hideCxoneOfflineBanner();
    }

    //============================================================
    // Enable c4-live-chat-button
    //============================================================

    private enableChatButton(): void {

        this.chatButtonEnabled = true;
        this.chatButtonVisible = true;

        console.log(
            '[CXone] c4-live-chat-button ENABLED'
        );
    }

    //============================================================
    // Disable c4-live-chat-button
    //============================================================

    private disableChatButton(): void {

        this.chatButtonEnabled = false;
        this.chatButtonVisible = false;

        console.log(
            '[CXone] c4-live-chat-button DISABLED'
        );
    }

    //============================================================
    // Load CXONE script dynamically
    //============================================================

    private loadCxoneChatWindow(): void {

        const win = window as any;

        // Already initialized
        if (
            win.cxoneInitialized ||
            this.cxoneInitialized
        ) {

            console.log(
                '[CXone] Already initialized'
            );

            this.setCxoneCustomFields();

            return;
        }

        // Already loading
        if (this.cxoneLoading) {

            console.log(
                '[CXone] Script is already loading'
            );

            return;
        }

        this.cxoneLoading = true;

        //==========================================================
        // CXONE bootstrap
        //==========================================================

        win.CXoneDfo = 'cxone';

        win.cxone =
            win.cxone ||
            function (...args: any[]): void {

                (
                    win.cxone.q =
                    win.cxone.q || []
                ).push(args);
            };

        win.cxone.u =
            this.CXONE_LOADER_URL;

        //==========================================================
        // Create script
        //==========================================================

        const script =
            document.createElement('script');

        script.type = 'module';

        script.src =
            `${this.CXONE_LOADER_URL}?${Math.round(
                Date.now() / 1000 / 3600
            )}`;

        console.log(
            '[CXone] Loading script:',
            script.src
        );

        //==========================================================
        // Script loaded
        //==========================================================

        script.onload = (): void => {

            console.log(
                '[CXone] Loader script loaded'
            );

            try {

                // Initialize CXONE
                win.cxone(
                    'init',
                    this.CXONE_TENANT_ID
                );

                // Initialize GUIDE
                win.cxone(
                    'guide',
                    'init'
                );

                this.cxoneInitialized = true;

                win.cxoneInitialized = true;

                console.log(
                    '[CXone] Initialized successfully'
                );

                // Set user information
                this.setCxoneCustomFields();

            } catch (error) {

                console.error(
                    '[CXone] Initialization failed:',
                    error
                );

            } finally {

                this.cxoneLoading = false;
            }
        };

        //==========================================================
        // Script failed
        //==========================================================

        script.onerror =
            (error: string | Event): void => {

                console.error(
                    '[CXone] Script failed to load:',
                    script.src,
                    error
                );

                this.cxoneLoading = false;
            };

        document.head.appendChild(script);
    }

    //============================================================
    // Set CXONE custom fields
    //============================================================

    private setCxoneCustomFields(): void {

        const win = window as any;

        if (
            !win.cxone ||
            !this.currentUser
        ) {

            console.warn(
                '[CXone] Unable to set custom fields'
            );

            return;
        }

        const firstName =
            this.currentUser.firstName || '';

        const lastName =
            (this.currentUser as any).lastName || '';

        const email =
            this.currentUser.email || '';

        const fullName =
            `${firstName} ${lastName}`.trim();

        try {

            // Customer name
            win.cxone(
                'guide',
                'setCustomFields',
                {
                    customerName: {
                        value: fullName,
                        hidden: false
                    }
                }
            );

            // Customer email
            win.cxone(
                'guide',
                'setCustomFields',
                {
                    contactCustomFields: [
                        {
                            ident: 'email',
                            value: email,
                            hidden: false
                        }
                    ]
                }
            );

        } catch (error) {

            console.error(
                '[CXone] Failed to set customer fields:',
                error
            );
        }
    }

    //============================================================
    // Hide CXONE chat icon
    //============================================================

    private hideCxoneChat(): void {

        if (this.cxoneStyleElement) {
            return;
        }

        const style =
            document.createElement('style');

        style.id =
            'cxone-offline-style';

        style.textContent = `
            [data-selector="GUIDE_CHANNEL_BUTTON"] {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                pointer-events: none !important;
            }

            [data-selector="GUIDE_CHANNEL_ICON"] {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
            }
        `;

        document.head.appendChild(style);

        this.cxoneStyleElement = style;

        console.log(
            '[CXone] Chat icon hidden'
        );
    }

    //============================================================
    // Show CXONE chat icon
    //============================================================

    private showCxoneChat(): void {

        if (!this.cxoneStyleElement) {
            return;
        }

        this.cxoneStyleElement.remove();

        this.cxoneStyleElement = null;

        console.log(
            '[CXone] Chat icon shown'
        );
    }

    //============================================================
    // Remove CXONE offline style
    //============================================================

    private removeCxoneOfflineStyle(): void {

        if (this.cxoneStyleElement) {

            this.cxoneStyleElement.remove();

            this.cxoneStyleElement = null;
        }
    }

    //============================================================
    // Hide CXONE offline banner
    //============================================================

    private hideCxoneOfflineBanner(): void {

        const banner =
            document.getElementById(
                'cxone-offline-banner'
            );

        if (banner) {

            banner.remove();

            console.log(
                '[CXone] Offline banner removed'
            );
        }
    }

    //============================================================
    // Schedule next time-based check
    //============================================================

    private scheduleNextCxoneTimeCheck(
        currentMinutes: number,
        startMinutes: number,
        endMinutes: number
    ): void {

        this.stopCxoneAvailabilityCheck();

        let minutesUntilChange: number;

        if (
            currentMinutes <
            startMinutes
        ) {

            // Before chat start time
            minutesUntilChange =
                startMinutes -
                currentMinutes;

        } else {

            // After chat end time
            // Next check = tomorrow start time
            minutesUntilChange =
                (24 * 60 - currentMinutes) +
                startMinutes;
        }

        console.log(
            '[CXone] Next time check in:',
            minutesUntilChange,
            'minutes'
        );

        this.cxoneStateTimer =
            setTimeout(() => {

                this.checkCxoneAvailability();

            },
                (minutesUntilChange *
                    60 *
                    1000) + 1000
            );
    }

    //============================================================
    // Schedule next day check
    //============================================================

    private scheduleNextCxoneDayCheck(): void {

        this.stopCxoneAvailabilityCheck();

        console.log(
            '[CXone] Weekend - checking again tomorrow'
        );

        this.cxoneStateTimer =
            setTimeout(() => {
                this.checkCxoneAvailability();
            },24 * 60 * 60 * 1000 );
    }

    //============================================================
    // Schedule agent availability check
    //============================================================

    private scheduleAgentAvailabilityCheck(): void {

        this.stopCxoneAvailabilityCheck();

        this.cxoneStateTimer =
            setTimeout(() => {

                this.checkCxoneAvailability();

            },
                5 * 60 * 1000
            );
    }

    //============================================================
    // Stop timer
    //============================================================

    private stopCxoneAvailabilityCheck(): void {

        if (this.cxoneStateTimer) {

            clearTimeout(
                this.cxoneStateTimer
            );

            this.cxoneStateTimer = null;
        }
    }

    //============================================================
    // Get Eastern Time
    //============================================================

    private getEasternTime(date: Date): {
        hour: number;
        minute: number;
        dayOfWeek: number;
    } {

        const formatter =
            new Intl.DateTimeFormat(
                'en-US',
                {
                    timeZone: 'America/New_York',
                    weekday: 'short',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: false
                }
            );

        const parts =
            formatter.formatToParts(date);

        let hour = 0;
        let minute = 0;
        let dayOfWeek = 0;

        for (
            let i = 0;
            i < parts.length;
            i++
        ) {

            if (parts[i].type === 'hour') {
                hour =
                    Number(parts[i].value);
            }

            if (parts[i].type === 'minute') {
                minute =
                    Number(parts[i].value);
            }

            if (parts[i].type === 'weekday') {

                switch (parts[i].value) {

                    case 'Sun':
                        dayOfWeek = 0;
                        break;

                    case 'Mon':
                        dayOfWeek = 1;
                        break;

                    case 'Tue':
                        dayOfWeek = 2;
                        break;

                    case 'Wed':
                        dayOfWeek = 3;
                        break;

                    case 'Thu':
                        dayOfWeek = 4;
                        break;

                    case 'Fri':
                        dayOfWeek = 5;
                        break;

                    case 'Sat':
                        dayOfWeek = 6;
                        break;
                }
            }
        }

        return {
            hour,
            minute,
            dayOfWeek
        };
    }

    //============================================================
    // Format time
    //============================================================

    private formatTime(
        hour: number,
        minute: number
    ): string {

        return `${hour
            .toString()
            .padStart(2, '0')}:${minute
            .toString()
            .padStart(2, '0')}`;
    }
}
